/**
 * 総合カタログ初回パネル(板選択と必要時の年齢確認)の完了状態をlocalStorageに記憶する。
 * `dismissedAnnouncements.ts` と同様、Storageが使えない環境(プライベート
 * モード等)でもクラッシュさせず「毎回初回扱い」へ縮退する。
 */

export const DOCKING_ONBOARDING_STORAGE_KEY = "nijiurachan-docking-onboarding";

const CURRENT_VERSION = 2;

export type DockingOnboardingRecord = Readonly<{
  v: number;
  completed: true;
  /** 18歳以上であることを自己申告したか。 */
  adult: boolean;
  completedAt: string;
}>;

type OnboardingStorage = Pick<Storage, "getItem" | "setItem">;
type RemovableStorage = Pick<Storage, "removeItem">;

/** 初回パネルを出すか。必要な案内を終えた記録があるときだけ出さない。 */
export function shouldShowDockingOnboarding(
  storage: OnboardingStorage | null = browserStorage(),
  requiresAdultConfirmation = false,
): boolean {
  const record = readRecord(storage);
  return record === null || (requiresAdultConfirmation && !record.adult);
}

export function completeDockingOnboarding(
  completedAt: string,
  adult: boolean,
  storage: OnboardingStorage | null = browserStorage(),
): void {
  if (!storage) return;
  const record: DockingOnboardingRecord = {
    v: CURRENT_VERSION,
    completed: true,
    adult,
    completedAt,
  };
  try {
    storage.setItem(DOCKING_ONBOARDING_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage拒否時もReactのstate側でパネルは閉じるため無視する
  }
}

/** 「全データをクリア」用。次回`/mix`アクセスで初回パネルへ戻す。 */
export function resetDockingOnboarding(
  storage: RemovableStorage | null = browserStorage(),
): void {
  if (!storage) return;
  try {
    storage.removeItem(DOCKING_ONBOARDING_STORAGE_KEY);
  } catch {
    // Storage拒否時は記録自体が残っていないため無視する
  }
}

function readRecord(
  storage: OnboardingStorage | null,
): DockingOnboardingRecord | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(DOCKING_ONBOARDING_STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    if (
      record.v === 1 &&
      record.adult === true &&
      typeof record.completedAt === "string"
    ) {
      return {
        v: CURRENT_VERSION,
        completed: true,
        adult: true,
        completedAt: record.completedAt,
      };
    }
    if (
      record.v !== CURRENT_VERSION ||
      record.completed !== true ||
      typeof record.adult !== "boolean" ||
      typeof record.completedAt !== "string"
    ) {
      return null;
    }
    return {
      v: CURRENT_VERSION,
      completed: true,
      adult: record.adult,
      completedAt: record.completedAt,
    };
  } catch {
    return null;
  }
}

function browserStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
